import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ReportTable } from '../components/ReportTable';
import {
  FileBarChart2, Loader2, Download, Link2, Printer, Save, Trash2, Search, ChevronRight,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('eventism_token')}` } });

export default function ReportsPage() {
  const [params, setParams] = useSearchParams();
  const [catalog, setCatalog] = useState([]);
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState(params.get('event') || '');
  const [report, setReport] = useState(params.get('report') || '');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [data, setData] = useState(null);
  const [running, setRunning] = useState(false);
  const [views, setViews] = useState([]);
  const [selectedView, setSelectedView] = useState('');

  useEffect(() => {
    axios.get(`${API}/reports/catalog`).then(r => setCatalog(r.data.categories)).catch(() => {});
    axios.get(`${API}/hosted-events?status=all&limit=200&include_drafts=true`, auth()).then(r => {
      const evs = r.data.events || r.data || [];
      setEvents(evs);
      if (!params.get('event') && evs[0]) setEventId(evs[0].event_id);
    }).catch(() => toast.error('Could not load your events'));
    axios.get(`${API}/reports/views`, auth()).then(r => setViews(r.data.views)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const run = useCallback(async (rep = report, ev = eventId, from = dateFrom, to = dateTo) => {
    if (!rep || !ev) return;
    setRunning(true);
    try {
      const r = await axios.get(`${API}/reports/run`, { ...auth(), params: { report: rep, event_id: ev, date_from: from, date_to: to } });
      setData(r.data);
      setParams({ event: ev, report: rep }, { replace: true });
    } catch (e) { toast.error(e.response?.data?.detail || 'Report failed'); }
    finally { setRunning(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report, eventId, dateFrom, dateTo]);

  useEffect(() => { if (report && eventId) run(report, eventId); }, [report, eventId]); // eslint-disable-line react-hooks/exhaustive-deps

  const exportFile = (format) => {
    const qs = new URLSearchParams({ report, event_id: eventId, format, date_from: dateFrom, date_to: dateTo });
    fetch(`${API}/reports/export?${qs}`, auth())
      .then(r => { if (!r.ok) throw new Error(); return r.blob(); })
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${data?.event?.name || 'event'} - ${data?.label || report}.${format}`;
        a.click();
      }).catch(() => toast.error('Export failed'));
  };

  const shareLink = async () => {
    try {
      const r = await axios.post(`${API}/reports/share`, { report, event_id: eventId, date_from: dateFrom, date_to: dateTo }, auth());
      const url = `${window.location.origin}${r.data.url}`;
      await navigator.clipboard.writeText(url).catch(() => {});
      toast.success('Read-only link copied — send it to your partner', { description: url, duration: 9000 });
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not create link'); }
  };

  const saveView = async () => {
    const name = window.prompt('Name this saved view (e.g. "Venue weekly sales")');
    if (!name) return;
    try {
      const r = await axios.post(`${API}/reports/views`, { name, report, event_id: eventId, date_from: dateFrom, date_to: dateTo }, auth());
      setViews(v => [r.data.view, ...v]);
      toast.success('View saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save view'); }
  };

  const eventName = useMemo(() => events.find(e => e.event_id === eventId)?.name || '', [events, eventId]);

  return (
    <div className="min-h-screen bg-background pt-20 pb-12 px-4" data-testid="reports-page">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-2 mb-1 print:hidden">
          <FileBarChart2 className="h-6 w-6 text-indigo-400" />
          <h1 className="font-heading text-2xl font-bold">Reports</h1>
        </div>
        <p className="text-sm text-muted-foreground mb-5 print:hidden">Corporate-ready reports for you and your partners — run, export, print or share.</p>

        {/* Controls */}
        <div className="flex flex-wrap items-center gap-2 mb-5 print:hidden">
          <select value={eventId} onChange={e => setEventId(e.target.value)}
            className="h-9 rounded-md border border-border bg-background px-3 text-sm min-w-[220px]" data-testid="reports-event-select">
            {events.map(e => <option key={e.event_id} value={e.event_id}>{e.name}</option>)}
          </select>
          <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-9 w-[150px]" data-testid="reports-date-from" />
          <span className="text-xs text-muted-foreground">to</span>
          <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-9 w-[150px]" data-testid="reports-date-to" />
          <Button size="sm" variant="outline" onClick={() => run()} disabled={!report || running} data-testid="reports-apply-dates">Apply</Button>
          {views.length > 0 && (
            <select value={selectedView} onChange={e => {
              const v = views.find(x => x.view_id === e.target.value);
              setSelectedView(e.target.value);
              if (v) {
                setEventId(v.event_id); setDateFrom(v.date_from || ''); setDateTo(v.date_to || ''); setReport(v.report);
                run(v.report, v.event_id, v.date_from || '', v.date_to || '');
              }
            }} className="h-9 rounded-md border border-border bg-background px-3 text-sm ml-auto" data-testid="reports-saved-views">
              <option value="">Saved views…</option>
              {views.map(v => <option key={v.view_id} value={v.view_id}>{v.name}</option>)}
            </select>
          )}
        </div>

        <div className="grid lg:grid-cols-[280px_1fr] gap-6">
          {/* Catalog */}
          <aside className="space-y-4 print:hidden" data-testid="reports-catalog">
            {catalog.map(cat => (
              <div key={cat.name}>
                <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-1.5">{cat.name}</p>
                <div className="space-y-0.5">
                  {cat.reports.map(r => (
                    <button key={r.key} onClick={() => setReport(r.key)}
                      className={`w-full text-left px-2.5 py-1.5 rounded-md text-sm flex items-center justify-between group ${report === r.key ? 'bg-indigo-500/15 text-indigo-300 font-semibold' : 'hover:bg-muted/50 text-foreground'}`}
                      title={r.description} data-testid={`report-item-${r.key}`}>
                      {r.label}
                      <ChevronRight className={`h-3.5 w-3.5 ${report === r.key ? 'opacity-100' : 'opacity-0 group-hover:opacity-50'}`} />
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </aside>

          {/* Result */}
          <main>
            {!report ? (
              <Card><CardContent className="py-20 text-center text-muted-foreground text-sm" data-testid="reports-empty">
                <FileBarChart2 className="h-10 w-10 mx-auto mb-3 opacity-40" />
                Pick a report from the left to run it for <strong>{eventName || 'your event'}</strong>.
              </CardContent></Card>
            ) : running && !data ? (
              <div className="py-20 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" /></div>
            ) : data ? (
              <div className="space-y-4">
                <div className="flex flex-wrap items-start justify-between gap-3 print:hidden">
                  <div>
                    <h2 className="text-lg font-bold">{data.label}</h2>
                    <p className="text-xs text-muted-foreground">{data.description} · {data.event?.name}</p>
                  </div>
                  <div className="flex gap-1.5 flex-wrap">
                    <Button size="sm" variant="outline" onClick={() => exportFile('csv')} data-testid="report-export-csv"><Download className="h-3.5 w-3.5 mr-1" /> CSV</Button>
                    <Button size="sm" variant="outline" onClick={() => exportFile('xlsx')} data-testid="report-export-xlsx"><Download className="h-3.5 w-3.5 mr-1" /> Excel</Button>
                    <Button size="sm" variant="outline" onClick={() => window.print()} data-testid="report-print"><Printer className="h-3.5 w-3.5 mr-1" /> Print / PDF</Button>
                    <Button size="sm" variant="outline" onClick={shareLink} className="text-indigo-300 border-indigo-500/40" data-testid="report-share"><Link2 className="h-3.5 w-3.5 mr-1" /> Share link</Button>
                    <Button size="sm" variant="outline" onClick={saveView} data-testid="report-save-view"><Save className="h-3.5 w-3.5 mr-1" /> Save view</Button>
                  </div>
                </div>
                <ReportTable data={data} />
              </div>
            ) : null}
          </main>
        </div>
      </div>
    </div>
  );
}
